import { Fragment, type ReactNode } from 'react';
import {
  tokenizeInlineMarkup,
  type InstrumentItem,
  type InstrumentSection,
} from '@cca/parishes-shared';

/**
 * Render instrument text with its lightweight markup
 * (`**bold**` / `*italic*` / `_underline_`) as React nodes. Text content comes
 * through React text nodes, so it is auto-escaped — no dangerouslySetInnerHTML.
 */
export function renderInstrumentText(body: string): ReactNode {
  return tokenizeInlineMarkup(body).map((t, i) => {
    if (t.bold) return <strong key={i}>{t.text}</strong>;
    if (t.italic) return <em key={i}>{t.text}</em>;
    if (t.underline) return <u key={i}>{t.text}</u>;
    return <Fragment key={i}>{t.text}</Fragment>;
  });
}

interface SectionView {
  /** Reflection questions that are not follow-ups, in section order. */
  topReflection: InstrumentItem[];
  /** Follow-ups keyed by their parent id (scorecard item or reflection question). */
  followUps: Map<string, InstrumentItem[]>;
}

/** Split a section's reflection list into top-level questions and the
 *  follow-ups nested under each parent (parents may be scale items too). */
export function partitionFollowUps(section: InstrumentSection): SectionView {
  const topReflection: InstrumentItem[] = [];
  const followUps = new Map<string, InstrumentItem[]>();
  for (const q of section.reflection) {
    if (q.parentId) {
      const list = followUps.get(q.parentId);
      if (list) list.push(q);
      else followUps.set(q.parentId, [q]);
    } else {
      topReflection.push(q);
    }
  }
  return { topReflection, followUps };
}
